import { Link } from "react-router-dom";
import {
    ArrowRight,
    Scale,
    Compass,
    Coins,
    BookOpen,
    Building2,
    Users,
    Globe,
    Layers,
    Mail,
    Phone,
    Shield,
    Star,
    Clock,
    UserCheck,
    Globe2,
} from "lucide-react";
import Reveal from "@/components/Reveal";

// ─────────────────────────────────────────────────────────────
// Ecosysteme — public page · valorise la profondeur du réseau
// XV Capital Partners sans dévoiler son identité (aucun logo,
// aucun nom, aucune photographie de partenaire).
// ─────────────────────────────────────────────────────────────

const HERO_IMG =
    "https://images.pexels.com/photos/2603464/pexels-photo-2603464.jpeg?auto=compress&cs=tinysrgb&w=2400";

const DOMAINS = [
    {
        icon: Scale,
        title: "Droit des affaires & transmission",
        text: "Avocats d'affaires, structuration juridique, fiscalité, opérations de croissance et de transmission.",
    },
    {
        icon: Compass,
        title: "Ingénierie patrimoniale",
        text: "Fiscalistes, ingénierie patrimoniale, structuration internationale et optimisation de long terme.",
    },
    {
        icon: Coins,
        title: "Finance & financement",
        text: "Banques privées, banques d'affaires, dette privée, investisseurs et solutions de financement.",
    },
    {
        icon: BookOpen,
        title: "Expertise comptable & audit",
        text: "Experts-comptables, audit, valorisation, due diligence et accompagnement financier.",
    },
    {
        icon: Building2,
        title: "Immobilier & actifs réels",
        text: "Conseils immobiliers, structuration d'actifs, valorisation et stratégies patrimoniales.",
    },
    {
        icon: Users,
        title: "Family Offices",
        text: "Accompagnement des familles entrepreneuriales, gouvernance et préservation patrimoniale.",
    },
    {
        icon: Globe,
        title: "Partenaires internationaux",
        text: "Correspondants et experts de confiance en Europe, Afrique, Moyen-Orient et Amérique du Nord.",
    },
    {
        icon: Layers,
        title: "Experts sectoriels",
        text: "Spécialistes indépendants mobilisés selon les enjeux spécifiques de chaque opération.",
    },
];

const TRUST_STATS = [
    {
        icon: Globe2,
        kpi: "International",
        label: "Cercle de correspondants en Europe, Afrique, Moyen-Orient et Amérique du Nord",
    },
    {
        icon: UserCheck,
        kpi: "Sélectif",
        label: "Experts mobilisés mandat par mandat selon les enjeux du dossier",
    },
    {
        icon: Layers,
        kpi: "Indépendant",
        label: "Professionnels libres de tout conflit d'intérêts",
    },
    {
        icon: Shield,
        kpi: "Confidentialité",
        label: "Engagement éthique absolu sur chaque opération coordonnée",
    },
    {
        icon: Star,
        kpi: "Excellence",
        label: "Reconnaissance dans chaque domaine d'expertise mobilisé",
    },
    {
        icon: Clock,
        kpi: "Réactivité",
        label: "Disponibilité et coordination directe par le cabinet",
    },
];

export default function Ecosysteme() {
    return (
        <div data-testid="ecosysteme-page" className="bg-xv-ivory">
            <EcosystemeHero />
            <EcosystemeIntro />
            <EcosystemeGrid />
            <EcosystemeTrust />
            <EcosystemeCTA />
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// HERO — split layout : texte gauche · image Monaco droite
// ─────────────────────────────────────────────────────────────
function EcosystemeHero() {
    return (
        <section
            data-testid="ecosysteme-hero"
            className="relative bg-xv-midnightDeep text-xv-ivory overflow-hidden pt-28 md:pt-32"
        >
            <div className="grid lg:grid-cols-2 min-h-[640px] lg:min-h-[680px]">
                {/* TEXT LEFT */}
                <div className="relative z-10 flex items-center px-6 md:px-12 lg:px-16 xl:px-24 py-20 lg:py-0">
                    <div className="max-w-[640px]">
                        <Reveal>
                            <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                                <span className="block h-px w-12 bg-xv-goldSoft" />
                                <span className="overline">
                                    Écosystème international
                                </span>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h1
                                className="font-serif text-xv-ivory text-[36px] sm:text-5xl lg:text-[58px] xl:text-[64px] leading-[1.04] tracking-tight"
                                style={{ fontWeight: 300 }}
                            >
                                Un réseau d'expertises au service des
                                situations complexes.
                            </h1>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <span className="mt-10 block h-px w-16 bg-xv-goldSoft" />
                        </Reveal>
                        <Reveal delay={0.3}>
                            <p className="mt-9 text-xv-ivory/80 text-[15.5px] md:text-[16.5px] leading-[1.9] font-light max-w-lg">
                                Chaque mandat mobilise un cercle d'experts
                                sélectionnés selon les enjeux du dossier.
                            </p>
                        </Reveal>
                        <Reveal delay={0.35}>
                            <p className="mt-6 text-xv-ivory/65 text-[14.5px] md:text-[15.5px] leading-[1.9] font-light max-w-lg">
                                XV Capital Partners coordonne et fédère, autour
                                de chaque opération, les compétences juridiques,
                                financières, patrimoniales et sectorielles
                                nécessaires à sa réussite.
                            </p>
                        </Reveal>
                    </div>
                </div>

                {/* IMAGE RIGHT */}
                <div className="relative h-[360px] lg:h-auto">
                    <img
                        src={HERO_IMG}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* soft fade on the left edge to blend with the text panel */}
                    <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-xv-midnightDeep to-transparent pointer-events-none" />
                    <div className="absolute inset-y-0 left-0 right-0 lg:hidden bg-xv-midnightDeep/35 pointer-events-none" />
                </div>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// INTRO — centered explanation
// ─────────────────────────────────────────────────────────────
function EcosystemeIntro() {
    return (
        <section
            data-testid="ecosysteme-intro"
            className="bg-xv-ivory py-24 md:py-32"
        >
            <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                <Reveal>
                    <h2
                        className="overline text-xv-black tracking-[0.32em] text-[12px] md:text-[13px]"
                        style={{ fontWeight: 500 }}
                    >
                        Notre écosystème
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <span className="mt-6 inline-block h-px w-14 bg-xv-gold" />
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-10 text-xv-text-secondary text-[16px] md:text-[17.5px] leading-[1.95] font-light max-w-3xl mx-auto">
                        Nous privilégions des relations de confiance construites
                        dans la durée avec des professionnels indépendants
                        partageant les mêmes exigences d'excellence, de
                        discrétion et d'engagement.
                    </p>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="mt-6 text-xv-text-secondary text-[15.5px] md:text-[16.5px] leading-[1.95] font-light max-w-2xl mx-auto">
                        Les expertises mobilisées varient selon la nature de
                        chaque mission et sont coordonnées directement par le
                        cabinet.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// GRID — 4 × 2 cards · thin border · gold outline icon · CTA
// ─────────────────────────────────────────────────────────────
function EcosystemeGrid() {
    return (
        <section
            data-testid="ecosysteme-domains"
            className="bg-xv-ivory pb-28 md:pb-40"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div
                    data-testid="ecosysteme-grid"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-xv-line border border-xv-line"
                >
                    {DOMAINS.map((d, i) => {
                        const Icon = d.icon;
                        return (
                            <Reveal key={d.title} delay={(i % 4) * 0.05}>
                                <article
                                    data-testid={`ecosysteme-card-${i}`}
                                    className="group bg-xv-ivory h-full px-8 md:px-9 py-12 md:py-14 flex flex-col items-center text-center hover:bg-xv-cream transition-colors duration-700"
                                >
                                    <Icon
                                        strokeWidth={1.1}
                                        size={46}
                                        className="text-xv-gold mb-9"
                                    />
                                    <h3
                                        className="overline text-xv-black tracking-[0.22em] text-[11.5px] md:text-[12px] leading-[1.5] mb-6 min-h-[36px] flex items-center"
                                        style={{ fontWeight: 500 }}
                                    >
                                        {d.title}
                                    </h3>
                                    <p className="text-xv-text-secondary text-[13.5px] md:text-[14px] leading-[1.85] font-light max-w-[260px]">
                                        {d.text}
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="mt-8 inline-flex items-center gap-2 text-xv-gold text-[11.5px] tracking-[0.18em] uppercase hover:text-xv-goldSoft transition-colors duration-500"
                                    >
                                        En savoir plus
                                        <ArrowRight
                                            strokeWidth={1.2}
                                            size={13}
                                            className="transition-transform duration-500 group-hover:translate-x-1"
                                        />
                                    </Link>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// TRUST / STATS BAND — dark BG · 6 reassurance pillars
// (jamais de chiffres faux : libellés qualitatifs uniquement)
// ─────────────────────────────────────────────────────────────
function EcosystemeTrust() {
    return (
        <section
            data-testid="ecosysteme-trust"
            className="bg-xv-midnightDeep text-xv-ivory py-24 md:py-32"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="text-center mb-16 md:mb-20">
                    <Reveal>
                        <h2
                            className="overline text-xv-ivory tracking-[0.32em] text-[12px] md:text-[13px]"
                            style={{ fontWeight: 500 }}
                        >
                            Nos engagements
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <span className="mt-6 inline-block h-px w-14 bg-xv-goldSoft" />
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-10 text-xv-ivory/75 text-[15.5px] md:text-[16.5px] leading-[1.9] font-light max-w-3xl mx-auto">
                            Nous coordonnons un réseau sélectif de partenaires
                            indépendants partageant nos exigences d'excellence,
                            de confidentialité et d'engagement.
                        </p>
                    </Reveal>
                </div>

                <div
                    data-testid="ecosysteme-stats"
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-xv-ivory/[0.08]"
                >
                    {TRUST_STATS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.kpi} delay={(i % 6) * 0.05}>
                                <div
                                    data-testid={`ecosysteme-stat-${i}`}
                                    className="bg-xv-midnightDeep h-full px-6 py-10 lg:py-12 flex flex-col items-center text-center"
                                >
                                    <Icon
                                        strokeWidth={1.1}
                                        size={28}
                                        className="text-xv-goldSoft mb-6"
                                    />
                                    <div
                                        className="font-serif text-[22px] md:text-[24px] text-xv-ivory leading-tight mb-4"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {s.kpi}
                                    </div>
                                    <p className="text-xv-ivory/65 text-[12.5px] md:text-[13px] leading-[1.75] font-light max-w-[180px]">
                                        {s.label}
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// CTA FOOTER BAND — cream BG · two columns (action + contact)
// ─────────────────────────────────────────────────────────────
function EcosystemeCTA() {
    return (
        <section
            data-testid="ecosysteme-cta"
            className="bg-xv-cream py-20 md:py-24"
        >
            <div className="max-w-[1300px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                {/* Left — invitation */}
                <Reveal>
                    <div className="flex items-start gap-7">
                        <span className="hidden sm:inline-flex shrink-0 items-center justify-center w-14 h-14 border border-xv-gold/40 text-xv-gold">
                            <Mail strokeWidth={1.1} size={20} />
                        </span>
                        <div>
                            <div className="overline text-xv-black tracking-[0.3em] mb-4">
                                Besoin d'être accompagné ?
                            </div>
                            <p className="text-xv-text-secondary text-[15px] leading-[1.85] font-light mb-7 max-w-md">
                                Discutons de vos enjeux avec un membre du
                                cabinet, en toute confidentialité.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="ecosysteme-cta-meeting"
                                className="group inline-flex items-center gap-4 px-8 py-4 border border-xv-midnight text-xv-midnight text-[11px] tracking-[0.3em] uppercase hover:bg-xv-midnight hover:text-xv-ivory transition-all duration-700"
                            >
                                Prendre rendez-vous
                                <ArrowRight
                                    strokeWidth={1.2}
                                    size={14}
                                    className="transition-transform duration-500 group-hover:translate-x-1"
                                />
                            </Link>
                        </div>
                    </div>
                </Reveal>

                {/* Right — direct contact */}
                <Reveal delay={0.1}>
                    <div className="lg:border-l lg:border-xv-line lg:pl-12 xl:pl-16">
                        <div className="overline text-xv-black tracking-[0.3em] mb-5">
                            Une question sur notre écosystème ?
                        </div>
                        <ul className="space-y-4 text-[14.5px] font-light text-xv-text-secondary">
                            <li className="flex items-center gap-3">
                                <Mail
                                    strokeWidth={1.2}
                                    size={15}
                                    className="text-xv-gold shrink-0"
                                />
                                <a
                                    href="mailto:contact@xv-capitalpartners.com"
                                    className="hover:text-xv-midnight transition-colors duration-500"
                                >
                                    contact@xv-capitalpartners.com
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone
                                    strokeWidth={1.2}
                                    size={15}
                                    className="text-xv-gold shrink-0"
                                />
                                <span>+33 (0)4 22 13 60 50</span>
                            </li>
                        </ul>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
